import {
	calculateTotal,
	ensureNegative,
	round,
} from '@shared/utils/math';
import { sanitizeNumber } from '@shared/utils/number';
import type {
	CargoStats,
	CargoWithPortsAndCalculationsFromDb,
	CargoWithPortsAndCalculationsJSON,
} from '@api/features/laytime/getCargosAndLaytimes';
import { CargoPortProps } from '@api/models/cargo-port';
import {
	LaytimeIntervals,
	LaytimeSettlementTypes,
	PortActionTypes,
} from '../constants';
import {
	isLoadPort,
	isDischargePort,
} from '../cargoPortHelpers';
import { sortTimesheet } from './helpers';

export const calculateReversibleLaytime = (
	cargo: CargoWithPortsAndCalculationsFromDb | CargoWithPortsAndCalculationsJSON,
	stats: CargoStats,
	includeSettlement = true,
) => {
	let totalDemurrage = 0;
	let totalDespatch = 0;
	let minutesDiff = 0;

	const allRows = cargo.Timesheets.flatMap((ts) => ts.TimesheetRows).filter((tr) => tr != null);
	const timesheetRows = sortTimesheet(allRows);

	const demurrageRow = timesheetRows?.find((tr) => tr.demurrageStarts);

	const demurragePerMinute = sanitizeNumber(cargo.demurrage) /
		(cargo.demurrageDespatchInterval === LaytimeIntervals.DAY ? 24 * 60 : 60);

	const despatchPerMinute = sanitizeNumber(cargo.despatch) /
		(cargo.demurrageDespatchInterval === LaytimeIntervals.DAY ? 24 * 60 : 60);

	const timeAllowedInMinutes = sanitizeNumber(cargo.timeAllowed) *
		(cargo.timeAllowedInterval === LaytimeIntervals.DAY ? 24 * 60 : 60);

	if (demurrageRow) {
		const demurrageRowIndex = timesheetRows.findIndex((tr) => tr.id === demurrageRow.id);
		const demurrageSlice = timesheetRows.slice(demurrageRowIndex);
		const minutesUsed = calculateTotal(demurrageSlice, (c) => c.totalCountingMinutes);
		minutesDiff = ensureNegative(minutesUsed);

		const dem = minutesUsed * demurragePerMinute;
		totalDemurrage += dem;
	}

	if (!demurrageRow && timesheetRows.length !== 0) {
		const minutesUsed = calculateTotal(timesheetRows, (c) => c.totalCountingMinutes);

		const des = (timeAllowedInMinutes - minutesUsed) * despatchPerMinute;
		totalDespatch += des;

		minutesDiff = timeAllowedInMinutes - minutesUsed;
	}

	const calculatedDemurrage = totalDemurrage;
	const calculatedDespatch = totalDespatch;

	let totalReceivable = round(Math.abs(totalDemurrage) - Math.abs(totalDespatch), 2);

	if (cargo.settlementAmount != null && includeSettlement) {
		if (cargo.settlementType === LaytimeSettlementTypes.DEMURRAGE) {
			totalDemurrage = cargo.settlementAmount;
			totalDespatch = 0;
		}

		if (cargo.settlementType === LaytimeSettlementTypes.DESPATCH) {
			totalDespatch = cargo.settlementAmount;
			totalDemurrage = 0;
		}

		totalReceivable = cargo.settlementAmount;
	}

	return {
		...stats,
		totalDemurrage: round(Math.abs(totalDemurrage), 2),
		totalDespatch: round(Math.abs(totalDespatch), 2),
		calculatedDemurrage,
		calculatedDespatch,
		totalReceivable,
		totalTimeDiff: minutesDiff,
		isSettlement: cargo.settlementAmount != null,
	};
};

export const calculateNonReversibleLaytime = (
	cargo: CargoWithPortsAndCalculationsFromDb | CargoWithPortsAndCalculationsJSON,
	stats: CargoStats,
	includeSettlement = true,
) => {
	const cargoPorts = cargo.CargoPorts;

	let demurrage = 0;
	let despatch = 0;

	const intervalInMinutes = cargo.demurrageDespatchInterval === LaytimeIntervals.DAY ? 24 * 60 : 60;

	cargoPorts.forEach((cargoPort) => {
		if (cargoPort.settlementAmount != null && includeSettlement) {
			if (cargoPort.settlementType === LaytimeSettlementTypes.DEMURRAGE) {
				demurrage += cargoPort.settlementAmount;
			} else {
				despatch += cargoPort.settlementAmount;
			}

			return;
		}

		const timesheetRows = sortTimesheet(cargoPort.Timesheet?.TimesheetRows ?? []);
		const demurrageRow = timesheetRows?.find((tr) => tr.demurrageStarts);

		if (timesheetRows.length === 0) {
			return;
		}

		const demurragePerMinute = sanitizeNumber(cargoPort.demurrage) / intervalInMinutes;
		const despatchPerMinute = sanitizeNumber(cargoPort.despatch) / intervalInMinutes;

		const timeAllowedInMinutes = sanitizeNumber(cargoPort.estimatedTimeAllowed) * intervalInMinutes;

		if (demurrageRow && cargoPort.laytimeTo) {
			const demurrageRowIndex = timesheetRows.findIndex((tr) => tr.id === demurrageRow.id);
			const demurrageSlice = timesheetRows.slice(demurrageRowIndex);
			const minutesUsed = calculateTotal(demurrageSlice, (c) => c.totalCountingMinutes);

			const dem = minutesUsed * demurragePerMinute;
			demurrage += dem;

			return;
		}

		const minutesUsed = calculateTotal(timesheetRows, (c) => c.totalCountingMinutes);
		const des = (timeAllowedInMinutes - minutesUsed) * despatchPerMinute;
		despatch += des;
	});

	return {
		...stats,
		totalDemurrage: round(Math.abs(demurrage), 2),
		totalDespatch: round(Math.abs(despatch), 2),
		calculatedDemurrage: 0,
		calculatedDespatch: 0,
		totalReceivable: round(Math.abs(demurrage) - Math.abs(despatch), 2),
		totalTimeDiff: 0 - 0,
		isSettlement: cargoPorts.every((cp) => cp.settlementAmount != null)
	};
};

export const calculateAveragedLaytime = (
	cargo: CargoWithPortsAndCalculationsFromDb | CargoWithPortsAndCalculationsJSON,
	stats: CargoStats,
	includeSettlement = true,
) => {
	let totalDemurrageLoading = 0;
	let totalDespatchLoading = 0;
	let totalDemurrageDischarging = 0;
	let totalDespatchDischarging = 0;
	let totalTimeLoading = 0;
	let totalTimeDischarging = 0;
	let isSettlementLoading = false;
	let isSettlementDischarging = false;

	const intervalInMinutes = cargo.demurrageDespatchInterval === LaytimeIntervals.DAY ? 24 * 60 : 60;

	let timeDiffLoading = 0;
	let timeDiffDischarging = 0;

	[PortActionTypes.LOADING, PortActionTypes.DISCHARGING].forEach((action) => {
		const func = action === PortActionTypes.LOADING ? isLoadPort : isDischargePort;

		// Casting to narrow and make array functions work
		const cargoPorts = cargo.CargoPorts as Array<CargoPortProps>;
		const cargoPortIds = cargoPorts.filter(func).map((cp) => cp.id);
		const relevantTimesheets = cargo.Timesheets.filter((ts) => cargoPortIds.includes(ts.cargoPortId!));
		const relevantRows = relevantTimesheets.flatMap((ts) => ts.TimesheetRows).filter((tr) => tr != null);
		const timesheetRows = sortTimesheet(relevantRows ?? []);

		if (timesheetRows.length === 0) {
			return;
		}

		const demurrageRow = timesheetRows?.find((tr) => tr.demurrageStarts);
		const totalTime = calculateTotal(timesheetRows, (c) => c.totalCountingMinutes);

		const demRate = action === PortActionTypes.LOADING ?
			sanitizeNumber(cargo.demurrageLoading) :
			sanitizeNumber(cargo.demurrageDischarge);
		const demurragePerMinute = demRate / intervalInMinutes;

		const despRate = action === PortActionTypes.LOADING ?
			sanitizeNumber(cargo.despatchLoading) :
			sanitizeNumber(cargo.despatchDischarge);
		const despatchPerMinute = despRate / intervalInMinutes;

		const timeAllowed = action === PortActionTypes.LOADING ?
			cargo.timeAllowedLoading :
			cargo.timeAllowedDischarging;
		const timeAllowedInMinutes = sanitizeNumber(timeAllowed) * intervalInMinutes;

		let demurrage = 0;
		let despatch = 0;

		if (demurrageRow) {
			const demurrageRowIndex = timesheetRows.findIndex((tr) => tr.id === demurrageRow.id);
			const demurrageSlice = timesheetRows.slice(demurrageRowIndex);
			const minutesUsed = calculateTotal(demurrageSlice, (c) => c.totalCountingMinutes);

			const dem = minutesUsed * demurragePerMinute;
			demurrage = dem;
		} else {
			const des = (timeAllowedInMinutes - totalTime) * despatchPerMinute;
			despatch = des;
		}

		const timeDiff = timeAllowedInMinutes - totalTime;

		if (action === PortActionTypes.LOADING) {
			totalDemurrageLoading = demurrage;
			totalDespatchLoading = despatch;
			totalTimeLoading = timeDiff;
			timeDiffLoading = timeDiff;
		} else {
			totalDemurrageDischarging = demurrage;
			totalDespatchDischarging = despatch;
			totalTimeDischarging = timeDiff;
			timeDiffDischarging = timeDiff;
		}
	});

	const calculatedDemurrageLoading = totalDemurrageLoading;
	const calculatedDespatchLoading = totalDespatchLoading;
	const calculatedDemurrageDischarging = totalDemurrageDischarging;
	const calculatedDespatchDischarging = totalDespatchDischarging;

	[PortActionTypes.LOADING, PortActionTypes.DISCHARGING].forEach((action) => {
		const settlementTypeKey = action === PortActionTypes.LOADING ?
			'settlementTypeLoading' :
			'settlementTypeDischarging';
		const settlementAmountKey = action === PortActionTypes.LOADING ?
			'settlementAmountLoading' :
			'settlementAmountDischarging';

		if (cargo[settlementAmountKey] != null && includeSettlement) {
			if (action === PortActionTypes.LOADING) {
				if (cargo[settlementTypeKey] === LaytimeSettlementTypes.DEMURRAGE) {
					totalDemurrageLoading = cargo[settlementAmountKey];
					totalDespatchLoading = 0;
				} else {
					totalDespatchLoading = cargo[settlementAmountKey];
					totalDemurrageLoading = 0;
				}

				isSettlementLoading = true;
			} else {
				if (cargo[settlementTypeKey] === LaytimeSettlementTypes.DEMURRAGE) {
					totalDemurrageDischarging = cargo[settlementAmountKey];
					totalDespatchDischarging = 0;
				} else {
					totalDespatchDischarging = cargo[settlementAmountKey];
					totalDemurrageDischarging = 0;
				}

				isSettlementDischarging = true;
			}
		}
	});

	const totalDemurrage = Math.abs(totalDemurrageLoading) + Math.abs(totalDemurrageDischarging);
	const totalDespatch = Math.abs(totalDespatchLoading) + Math.abs(totalDespatchDischarging);

	return {
		...stats,
		totalDemurrage: round(totalDemurrage, 2),
		totalDespatch: round(totalDespatch, 2),
		totalReceivable: round(totalDemurrage - totalDespatch, 2),
		totalTimeLoading,
		totalTimeDischarging,
		totalDemurrageLoading: round(Math.abs(totalDemurrageLoading), 2),
		totalDemurrageDischarging: round(Math.abs(totalDemurrageDischarging), 2),
		totalDespatchLoading: round(Math.abs(totalDespatchLoading), 2),
		totalDespatchDischarging: round(Math.abs(totalDespatchDischarging), 2),
		calculatedDemurrageLoading,
		calculatedDemurrageDischarging,
		calculatedDespatchLoading,
		calculatedDespatchDischarging,
		timeDiffLoading,
		timeDiffDischarging,
		isSettlementLoading,
		isSettlementDischarging,
	};
};
